<script setup>
import { storeToRefs } from 'pinia'
import Settings from 'assets/icons/settings.svg'
import Preferences from 'assets/icons/preferences.svg'
import Faq from 'assets/icons/faq.svg'
import Agb from 'assets/icons/agb.svg'
import Legal from 'assets/icons/legal.svg'
import Imprint from 'assets/icons/imprint.svg'
import DataProtection from 'assets/icons/data-protection.svg'
import Cookie from 'assets/icons/cookie.svg'
import { useMessageStore } from '~/stores/messages'
import { useAuthStore } from '~/stores/auth'
import { signIn, signOut } from '~/composables/useMsal'

// Fetch messages
const { messages: staticKeys } = storeToRefs(useMessageStore())

// Auth store
const authStore = useAuthStore()
const { isLoggedIn } = storeToRefs(authStore)

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Load user data
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')

const { data: currentUser } = useFetchProfile({ bearerToken, oid })

// Fetch data
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    await centerDetailSuspense()
})

// Button logout config
const logoutButtonConfig = computed(() => {
    return {
        buttonType: 'secondary',
        type: 'div',
        active: true,
        large: true,
        fn: async () => await signOut(route)
    }
})

const handleLogin = async () => {
    await signIn({
        center: centerRoute.value,
        centerName: centerDetails.value?.centerName
    })
}

// Button login config
const loginButtonConfig = computed(() => {
    return {
        buttonType: 'primary',
        type: 'div',
        large: true,
        active: true,
        fn: async () => handleLogin()
    }
})

const localePath = useLocalePath()

// Define items
const accountOverviewItems = computed(() => {
    return [
        {
            label: staticKeys.value?.menu_personaldata,
            fn: async () => await navigateWithSidebarQuery({
                query: { 'profile-edit': null },
                route
            }),
            loginStatus: isLoggedIn.value,
            restricted: true,
            icon: Settings
        },
        {
            label: staticKeys.value?.menu_preferences,
            fn: async () => await navigateWithSidebarQuery({ query: { 'profile-preferences': null }, route }),
            loginStatus: isLoggedIn.value,
            restricted: true,
            icon: Preferences
        },
        {
            label: staticKeys.value?.menu_imprint,
            fn: async () => await navigateTo(centerRoute.value ? localePath('centerUid-imprint') : localePath('imprint')),
            restricted: false,
            icon: Imprint
        },
        {
            label: staticKeys.value?.menu_dataprotection,
            fn: async () => await navigateTo(centerRoute.value ? localePath('centerUid-dataProtection') : localePath('dataProtection')),
            restricted: false,
            icon: DataProtection
        },
        {
            label: staticKeys.value?.menu_agb,
            fn: async () => await navigateTo(centerRoute.value ? localePath('centerUid-terms') : localePath('terms')),
            restricted: false,
            icon: Agb
        },
        {
            label: staticKeys.value?.menu_faq,
            fn: async () => await navigateTo(centerRoute.value ? localePath('centerUid-faq') : localePath('faq')),
            restricted: false,
            icon: Faq
        },
        {
            label: staticKeys.value?.menu_cookies,
            fn: () => showCMP(),
            restricted: false,
            icon: Cookie
        },
        {
            label: staticKeys.value?.menu_legal,
            fn: async () => await navigateTo(centerRoute.value ? localePath('centerUid-legal') : localePath('legal')),
            restricted: false,
            icon: Legal
        }
    ]
})

const headlineConfig = computed(() => {
    const firstName = currentUser.value?.data?.firstName
    const lastName = currentUser.value?.data?.lastName

    // Use a proper check for firstName and lastName
    const salutation = firstName && lastName ? `${firstName} _${lastName}_` : firstName || lastName || ''

    return {
        title: salutation || staticKeys.value?.account_headline_web_markdown,
    }
})

/**
 * Handles the deletion of the account
 * @returns {Promise<void>}
 */
const handleAccountDeletion = async () => {
    await navigateWithSidebarQuery({ query: { 'profile-delete': null }, route })
}
</script>

<template>
    <SideBarWrapper>
        <SideBarHeader>
            <template v-if="isLoggedIn">
                <SideBarMainHeadline :sidebar-main-headline-config="headlineConfig" />
            </template>
            <template v-else>
                <SideBarMainHeadline :sidebar-main-headline-config="{ title: staticKeys?.menu_greetings_headline }" />

                <p class="account-overview-intro">
                    {{ staticKeys?.menu_greetings_subheadline }}
                </p>

                <div class="login-button-bar">
                    <BaseButton :base-button-config="loginButtonConfig">
                        {{ staticKeys?.account_login }}
                    </BaseButton>
                </div>
            </template>
        </SideBarHeader>

        <ul class="account-overview-navigation">
            <UserAccountOverviewItem
                v-for="(item, index) in accountOverviewItems"
                :key="`overview-item-${index}`"
                :user-account-overview-item-config="item"
            />
            <!--
            <li>{{ staticKeys?.menu_parkingbills }}</li>
             <li>{{ staticKeys?.menu_valuecard }}</li>
            <li>{{ staticKeys?.menu_pushmessages }}</li>
            -->
        </ul>

        <BaseButton v-if="isLoggedIn" :base-button-config="logoutButtonConfig">
            {{ staticKeys?.account_logout }}
        </BaseButton>

        <div
            v-if="isLoggedIn"
            class="delete-account-link"
            @click="handleAccountDeletion"
        >
            {{ staticKeys?.menu_deleteaccount }}
        </div>

        <DevOnly>
            <pre>{{ useState('state_bearerToken') }}</pre>
            <pre>{{ useState('state_userOid') }}</pre>
            <pre>{{ currentUser }}</pre>
        </DevOnly>
    </SideBarWrapper>
</template>

<style lang="scss">
.delete-account-link {
    display: flex;
    justify-content: center;

    @include textlink($margin: 30px 0 0);
}

.password-reset-link {
    display: flex;
    justify-content: center;
    appearance: none;
    border: 0 none;
    background: transparent;
    text-align: center;
    cursor: pointer;

    @include textlink($margin: 20px auto 40px);
}

.account-overview-intro {
    margin: 0;

    @include body2-medium;
}

.login-button-bar {
    margin: 40px 0 30px;
}

.account-overview-navigation {
    list-style-type: none;
    padding: 0;
    margin: 0 0 44px;

    li {
        margin: 0 0 8px;
    }
}
</style>
